import Config from "@/common/config";
import type { KnownPermissionName } from "@/common/types/permission";
import type { User } from "@/common/types/user";
import router, { RouteNames } from "@/router";
import { useAuthStore } from "@/stores/auth";
import type { JWTVerifyResult } from "jose";
import { jwtVerify } from "jose";

// for the token / crypto stuff see https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/importKey
function str2ab(str: string) {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
}

function pemToCryptoKey(pem: string) {
  const pemHeader = "-----BEGIN PUBLIC KEY-----";
  const pemFooter = "-----END PUBLIC KEY-----";
  const pemContents = pem.substring(
    pemHeader.length,
    pem.length - pemFooter.length,
  );
  // base64 decode the string to get the binary data
  const binaryDerString = window.atob(pemContents);
  // convert from a binary string to an ArrayBuffer
  const binaryDer = str2ab(binaryDerString);

  return window.crypto.subtle.importKey(
    "spki",
    binaryDer,
    {
      name: "RSASSA-PKCS1-v1_5",
      hash: "SHA-256",
    },
    true,
    ["verify"],
  );
}

const cryptoKey = pemToCryptoKey(Config.tokenPublicKey);

export async function verifyToken(token: string): Promise<JWTVerifyResult> {
  return await jwtVerify(token, await cryptoKey);
}

export function stringify(value: unknown): string {
  switch (typeof value) {
    case "number":
    case "bigint":
    case "string":
    case "boolean":
    case "symbol":
    case "function":
      return value.toString();
    case "undefined":
      return "";
    case "object":
      if (value === null) {
        return "";
      } else if (value instanceof Date) {
        return value.toISOString();
      } else if (Array.isArray(value)) {
        return value.toString();
      } else {
        return JSON.stringify(value);
      }
  }
}

/**
 * @summary Converts an absolute URL to a relative URL by removing all leading slashes.
 *
 * When `baseURL` is set on the axios instance all relative URLs are resolved relative to it.
 * Absolute URLs are still resolved as if they were a full URL.
 *
 * @param url The URL to make relative.
 * @return The relative URL.
 */
export function toRelative(url: string): string {
  return url.replace(/^\/*/, "");
}

/**
 * Fills an URL placeholder with it's content.
 *
 * The placeholder in the URL is expected to be in the format `{placeholderName}`.
 *
 * @example
 * // returns "api/users/banana"
 * fillURLPlaceholder("api/users/{id}", "id", "banana")
 *
 * @param url The URL containing the placeholders to fill.
 * @param placeholder The name of th placeholder to fill.
 * @param value The value to fill the placeholder with.
 *
 * @return The URL with the placeholders filled.
 */
export function fillURLPlaceholder(
  url: string,
  placeholder: string,
  value: string,
): string {
  return url.replace(`{${placeholder}}`, value);
}

export function fillMultipleURLPlaceholders(
  url: string,
  placeholders: { [key: string]: string },
): string {
  let result = url;
  for (const placeholder in placeholders) {
    result = result.replace(`{${placeholder}}`, placeholders[placeholder]);
  }
  return result;
}

/**
 * Determines whether a user has a certain permission
 *
 * @param user the user to check for the permission
 * @param permissionName the name of the permission
 * @return true if the user has the permission, false otherwise
 */
export function userHasPermission(
  user: User,
  permissionName: KnownPermissionName,
): boolean {
  return user.permissions
    .map((permission) => permission.permissionName)
    .includes(permissionName);
}

/**
 * Returns the path to a randomly chosen background picture for et-connect or the compass picture if Config.isCompass is set to true
 */
export async function getRandomBackgroundPicturePath(isCompass?: boolean) {
  let imagePath;

  if (isCompass) {
    const compassPicture = Object.values(
      import.meta.glob<{ default: string }>("../assets/compass-login.png"),
    )[0];
    imagePath = (await compassPicture()).default;
  } else {
    const backgroundPictures = Object.values(
      import.meta.glob<{ default: string }>(
        "../assets/stock-images/background/**",
      ),
    );
    const randomPicture =
      backgroundPictures[Math.floor(Math.random() * backgroundPictures.length)];
    imagePath = (await randomPicture()).default;
  }

  return imagePath;
}

/**
 * Redirects the user to their home route or the original route they attempted to access.
 *
 * This function checks if there is a redirect query parameter in the current route.
 * If a redirect query parameter is present, it navigates to that route.
 * Otherwise, it navigates to the user's home route, which is determined by the user's home route name in the auth store.
 * If the user's home route name is not set, it defaults to the dashboard route.
 *
 * @function forwardToHome
 */

export function forwardToHome(): void {
  const authStore = useAuthStore();
  const redirect = router.currentRoute.value.query.redirect;

  if (redirect) {
    router.push(redirect as string);
  } else {
    const userHomeRouteName =
      authStore.userHomeRouteName ?? RouteNames.DASHBOARD;

    router.push({
      name: userHomeRouteName,
      params: { id: authStore.currentUser?.id },
    });
  }
}
